import { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  Edit2,
  Trash2,
  Check,
  X,
  Star,
  AlertCircle,
  CheckCircle,
  Mail,
  Server,
  ChevronDown,
  ChevronRight,
  Plug,
  Loader2,
  Eye,
  EyeOff,
  HelpCircle,
  ExternalLink,
  RefreshCw,
} from 'lucide-react';
import { Button, Card, Spinner, useToast } from '@centraoneplus/ui';
import { channelService } from '@/services/channel.service';
import type { EmailConfig } from '@/types/channel';
import { ThemedSelect } from '@components/common/ThemedSelect';
import { UndoSendWindowSetting } from './UndoSendWindowSetting';

/** Extract a user-friendly message from an API error without breaking on unknown shapes. */
function readApiError(err: any, fallback: string): string {
  return (
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    fallback
  );
}

// Provider options
const PROVIDERS: { value: string; label: string; description: string }[] = [
  { value: 'smtp', label: 'SMTP', description: 'Standard email protocol' },
  { value: 'sendgrid', label: 'SendGrid', description: 'SendGrid email service' },
  { value: 'ses', label: 'AWS SES', description: 'Amazon Simple Email Service' },
  { value: 'mailgun', label: 'Mailgun', description: 'Mailgun email service' },
  { value: 'microsoft_graph', label: 'Microsoft Graph', description: 'Microsoft 365 email' },
  { value: 'zeptomail', label: 'Zeptomail', description: 'Zoho Zeptomail transactional email' },
];

interface EmailFormData {
  name: string;
  email: string;
  provider: string;
  smtpHost: string;
  smtpPort: string;
  smtpUser: string;
  smtpPassword: string;
  smtpSecure: boolean;
  imapHost: string;
  imapPort: string;
  imapUser: string;
  imapPassword: string;
  imapSecure: boolean;
  signature: string;
  isDefault: boolean;
  // Provider-specific configs
  sendgridApiKey: string;
  sesRegion: string;
  sesAccessKeyId: string;
  sesSecretAccessKey: string;
  mailgunApiKey: string;
  mailgunDomain: string;
  graphClientId: string;
  graphClientSecret: string;
  graphTenantId: string;
  zeptomailApiKey: string;
}

const defaultFormData: EmailFormData = {
  name: '',
  email: '',
  provider: 'smtp',
  smtpHost: '',
  smtpPort: '587',
  smtpUser: '',
  smtpPassword: '',
  smtpSecure: true,
  imapHost: '',
  imapPort: '993',
  imapUser: '',
  imapPassword: '',
  imapSecure: true,
  signature: '',
  isDefault: false,
  sendgridApiKey: '',
  sesRegion: 'us-east-1',
  sesAccessKeyId: '',
  sesSecretAccessKey: '',
  mailgunApiKey: '',
  mailgunDomain: '',
  graphClientId: '',
  graphClientSecret: '',
  graphTenantId: '',
  zeptomailApiKey: '',
};

export default function EmailChannelSettings() {
  const toast = useToast();
  const [configs, setConfigs] = useState<EmailConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedConfig, setExpandedConfig] = useState<string | null>(null);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingConfig, setEditingConfig] = useState<EmailConfig | null>(null);
  const [formData, setFormData] = useState<EmailFormData>(defaultFormData);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Password visibility
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});

  // Testing state
  const [testingConfig, setTestingConfig] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ configId: string; success: boolean; message: string } | null>(null);

  // Form dirty state - tracks if form has unsaved changes
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [originalFormData, setOriginalFormData] = useState<EmailFormData>(defaultFormData);

  // Initialization state for Microsoft Graph
  const [isInitializing, setIsInitializing] = useState(false);
  const [initResult, setInitResult] = useState<{ success: boolean; message: string } | null>(null);

  const fetchConfigs = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await channelService.getEmailConfigs();
      setConfigs(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load email configurations');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  // Track form dirty state
  useEffect(() => {
    if (showForm) {
      const isDirty = JSON.stringify(formData) !== JSON.stringify(originalFormData);
      setIsFormDirty(isDirty);
    }
  }, [formData, originalFormData, showForm]);

  const handleCreateNew = () => {
    setEditingConfig(null);
    setFormData(defaultFormData);
    setOriginalFormData(defaultFormData);
    setShowForm(true);
    setFormError(null);
    setIsFormDirty(false);
    setInitResult(null);
    setTestResult(null);
  };

  const handleEdit = (config: EmailConfig) => {
    setEditingConfig(config);
    const initialFormData: EmailFormData = {
      name: config.name,
      email: config.email,
      provider: config.provider || 'smtp',
      smtpHost: config.smtpHost || '',
      smtpPort: config.smtpPort?.toString() || '587',
      smtpUser: config.smtpUser || '',
      smtpPassword: '',
      smtpSecure: config.smtpSecure ?? true,
      imapHost: config.imapHost || '',
      imapPort: config.imapPort?.toString() || '993',
      imapUser: config.imapUser || '',
      imapPassword: '',
      imapSecure: config.imapSecure ?? true,
      signature: config.signature || '',
      isDefault: config.isDefault,
      sendgridApiKey: config.providerConfig?.apiKey || '',
      sesRegion: config.providerConfig?.region || 'us-east-1',
      sesAccessKeyId: config.providerConfig?.accessKeyId || '',
      sesSecretAccessKey: '',
      mailgunApiKey: '',
      mailgunDomain: config.providerConfig?.domain || '',
      graphClientId: config.providerConfig?.clientId || '',
      graphClientSecret: '',
      graphTenantId: config.providerConfig?.tenantId || '',
      zeptomailApiKey: '',
    };
    setFormData(initialFormData);
    setOriginalFormData(initialFormData);
    setShowForm(true);
    setFormError(null);
    setIsFormDirty(false);
    setInitResult(null);
    setTestResult(null);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingConfig(null);
    setFormData(defaultFormData);
    setOriginalFormData(defaultFormData);
    setFormError(null);
    setIsFormDirty(false);
    setInitResult(null);
    setTestResult(null);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setFormError('Name is required');
      return;
    }
    if (!formData.email.trim()) {
      setFormError('Email address is required');
      return;
    }

    setIsSaving(true);
    setFormError(null);

    try {
      const providerConfig: Record<string, any> = {};

      // Build provider-specific config
      switch (formData.provider) {
        case 'sendgrid':
          if (formData.sendgridApiKey) {
            providerConfig.apiKey = formData.sendgridApiKey;
          }
          break;
        case 'ses':
          providerConfig.region = formData.sesRegion;
          if (formData.sesAccessKeyId) {
            providerConfig.accessKeyId = formData.sesAccessKeyId;
          }
          if (formData.sesSecretAccessKey) {
            providerConfig.secretAccessKey = formData.sesSecretAccessKey;
          }
          break;
        case 'mailgun':
          if (formData.mailgunApiKey) {
            providerConfig.apiKey = formData.mailgunApiKey;
          }
          if (formData.mailgunDomain) {
            providerConfig.domain = formData.mailgunDomain;
          }
          break;
        case 'microsoft_graph':
          if (formData.graphClientId) {
            providerConfig.clientId = formData.graphClientId;
          }
          if (formData.graphClientSecret) {
            providerConfig.clientSecret = formData.graphClientSecret;
          }
          if (formData.graphTenantId) {
            providerConfig.tenantId = formData.graphTenantId;
          }
          break;
        case 'zeptomail':
          if (formData.zeptomailApiKey) {
            providerConfig.apiKey = formData.zeptomailApiKey;
          }
          break;
      }

      const payload = {
        name: formData.name,
        email: formData.email,
        provider: formData.provider,
        ...(formData.provider === 'smtp' && {
          smtpHost: formData.smtpHost || undefined,
          smtpPort: formData.smtpPort ? parseInt(formData.smtpPort) : undefined,
          smtpUser: formData.smtpUser || undefined,
          smtpPassword: formData.smtpPassword || undefined,
          smtpSecure: formData.smtpSecure,
          imapHost: formData.imapHost || undefined,
          imapPort: formData.imapPort ? parseInt(formData.imapPort) : undefined,
          imapUser: formData.imapUser || undefined,
          imapPassword: formData.imapPassword || undefined,
          imapSecure: formData.imapSecure,
        }),
        providerConfig: Object.keys(providerConfig).length > 0 ? providerConfig : undefined,
        signature: formData.signature || undefined,
        isDefault: formData.isDefault,
      };

      let savedConfigId: string;

      if (editingConfig) {
        const updated = await channelService.updateEmailConfig(editingConfig.id, payload);
        savedConfigId = updated.id;
      } else {
        const created = await channelService.createEmailConfig(payload);
        savedConfigId = created.id;
      }

      // Auto-initialize Microsoft Graph after save
      if (formData.provider === 'microsoft_graph') {
        setIsInitializing(true);
        setInitResult(null);
        try {
          const initResponse = await channelService.initializeMicrosoftGraph(savedConfigId);
          if (initResponse.success && initResponse.accessTokenObtained) {
            setInitResult({ success: true, message: 'Microsoft Graph connection initialized successfully. You can now test the connection.' });
          } else {
            setInitResult({ success: false, message: initResponse.error || 'Failed to initialize Microsoft Graph connection' });
          }
        } catch (initErr: any) {
          setInitResult({ success: false, message: initErr.message || 'Failed to initialize Microsoft Graph' });
        } finally {
          setIsInitializing(false);
        }
      }

      await fetchConfigs();

      // Update original form data to reflect saved state (form is no longer dirty)
      setOriginalFormData(formData);
      setIsFormDirty(false);

      // Don't close form for Microsoft Graph - let user test connection
      if (formData.provider !== 'microsoft_graph') {
        handleCancel();
      } else {
        // Update editingConfig with the saved ID for testing
        const updatedConfigs = await channelService.getEmailConfigs();
        const savedConfig = updatedConfigs.find(c => c.id === savedConfigId);
        if (savedConfig) {
          setEditingConfig(savedConfig);
        }
      }

      toast.success(
        editingConfig ? 'Email config updated' : 'Email config created',
        `"${formData.name}" has been saved.`,
      );
    } catch (err: any) {
      setFormError(err.message || 'Failed to save email configuration');
      toast.error('Save failed', readApiError(err, 'Could not save the email configuration.'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (configId: string) => {
    if (!confirm('Are you sure you want to delete this email configuration?')) {
      return;
    }

    try {
      await channelService.deleteEmailConfig(configId);
      await fetchConfigs();
      toast.success('Email config deleted', 'The email configuration has been removed.');
    } catch (err: any) {
      setError(err.message || 'Failed to delete email configuration');
      toast.error('Delete failed', readApiError(err, 'Could not delete the email configuration.'));
    }
  };

  const handleSetDefault = async (configId: string) => {
    try {
      await channelService.setDefaultEmailConfig(configId);
      await fetchConfigs();
      toast.success('Default inbox updated', 'This inbox is now the default for new emails.');
    } catch (err: any) {
      setError(err.message || 'Failed to set default email configuration');
      toast.error('Update failed', readApiError(err, 'Could not set this as the default email configuration.'));
    }
  };

  const handleTest = async (configId: string) => {
    setTestingConfig(configId);
    setTestResult(null);

    try {
      const result = await channelService.testEmailConfig(configId);
      setTestResult({
        configId,
        success: result.success,
        message: result.message,
      });
    } catch (err: any) {
      setTestResult({
        configId,
        success: false,
        message: err.message || 'Test failed',
      });
    } finally {
      setTestingConfig(null);
    }
  };

  const togglePasswordVisibility = (field: string) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Email Channels</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">Configure email accounts for sending and receiving tickets</p>
        </div>
        <Button onClick={handleCreateNew}>
          <Plus className="h-4 w-4 mr-2" />
          Add Email Account
        </Button>
      </div>

      {/* Error message */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 text-red-700 rounded-lg">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Email Configuration Form */}
      {showForm && (
        <Card className="p-6">
          <h3 className="text-md font-medium text-gray-900 mb-4 dark:text-gray-100">
            {editingConfig ? 'Edit Email Account' : 'New Email Account'}
          </h3>

          {formError && (
            <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 text-red-700 rounded-lg text-sm">
              <AlertCircle className="h-4 w-4" />
              <span>{formError}</span>
            </div>
          )}

          <div className="space-y-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                  Account Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700"
                  placeholder="e.g., Main Support"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                  Email Address *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700"
                  placeholder="support@company.com"
                  disabled={!!editingConfig}
                />
              </div>
            </div>

            {/* Provider Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                Email Provider
              </label>
              <ThemedSelect
                value={formData.provider}
                onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700"
              >
                {PROVIDERS.map((provider) => (
                  <option key={provider.value} value={provider.value}>
                    {provider.label} - {provider.description}
                  </option>
                ))}
              </ThemedSelect>
            </div>

            {/* SMTP Configuration */}
            {formData.provider === 'smtp' && (
              <>
                <div className="border-t border-gray-200 pt-4 dark:border-gray-700">
                  <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2 dark:text-gray-100">
                    <Server className="h-4 w-4" />
                    SMTP Settings (Outgoing)
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                        SMTP Host
                      </label>
                      <input
                        type="text"
                        value={formData.smtpHost}
                        onChange={(e) => setFormData({ ...formData, smtpHost: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700"
                        placeholder="smtp.example.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                        SMTP Port
                      </label>
                      <input
                        type="number"
                        value={formData.smtpPort}
                        onChange={(e) => setFormData({ ...formData, smtpPort: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700"
                        placeholder="587"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                        Username
                      </label>
                      <input
                        type="text"
                        value={formData.smtpUser}
                        onChange={(e) => setFormData({ ...formData, smtpUser: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700"
                        placeholder="username"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                        Password
                      </label>
                      <div className="relative">
                        <input
                          type={showPasswords.smtpPassword ? 'text' : 'password'}
                          value={formData.smtpPassword}
                          onChange={(e) => setFormData({ ...formData, smtpPassword: e.target.value })}
                          className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700"
                          placeholder={editingConfig ? '••••••••' : 'password'}
                        />
                        <button
                          type="button"
                          onClick={() => togglePasswordVisibility('smtpPassword')}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-500"
                        >
                          {showPasswords.smtpPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                  </div>
                  <label className="flex items-center gap-2 mt-3 text-sm text-gray-700 dark:text-gray-200">
                    <input
                      type="checkbox"
                      checked={formData.smtpSecure}
                      onChange={(e) => setFormData({ ...formData, smtpSecure: e.target.checked })}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-700"
                    />
                    Use SSL/TLS
                  </label>
                </div>

                <div className="border-t border-gray-200 pt-4 dark:border-gray-700">
                  <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2 dark:text-gray-100">
                    <Mail className="h-4 w-4" />
                    IMAP Settings (Incoming)
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                        IMAP Host
                      </label>
                      <input
                        type="text"
                        value={formData.imapHost}
                        onChange={(e) => setFormData({ ...formData, imapHost: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700"
                        placeholder="imap.example.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                        IMAP Port
                      </label>
                      <input
                        type="number"
                        value={formData.imapPort}
                        onChange={(e) => setFormData({ ...formData, imapPort: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700"
                        placeholder="993"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                        Username
                      </label>
                      <input
                        type="text"
                        value={formData.imapUser}
                        onChange={(e) => setFormData({ ...formData, imapUser: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700"
                        placeholder="username"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                        Password
                      </label>
                      <div className="relative">
                        <input
                          type={showPasswords.imapPassword ? 'text' : 'password'}
                          value={formData.imapPassword}
                          onChange={(e) => setFormData({ ...formData, imapPassword: e.target.value })}
                          className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700"
                          placeholder={editingConfig ? '••••••••' : 'password'}
                        />
                        <button
                          type="button"
                          onClick={() => togglePasswordVisibility('imapPassword')}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-500"
                        >
                          {showPasswords.imapPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                  </div>
                  <label className="flex items-center gap-2 mt-3 text-sm text-gray-700 dark:text-gray-200">
                    <input
                      type="checkbox"
                      checked={formData.imapSecure}
                      onChange={(e) => setFormData({ ...formData, imapSecure: e.target.checked })}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-700"
                    />
                    Use SSL/TLS
                  </label>
                </div>
              </>
            )}

            {/* SendGrid Configuration */}
            {formData.provider === 'sendgrid' && (
              <div className="border-t border-gray-200 pt-4 dark:border-gray-700">
                <h4 className="text-sm font-medium text-gray-900 mb-3 dark:text-gray-100">SendGrid Configuration</h4>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                    API Key
                  </label>
                  <div className="relative">
                    <input
                      type={showPasswords.sendgridApiKey ? 'text' : 'password'}
                      value={formData.sendgridApiKey}
                      onChange={(e) => setFormData({ ...formData, sendgridApiKey: e.target.value })}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700"
                      placeholder="SG.xxxxxxxx"
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('sendgridApiKey')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-500"
                    >
                      {showPasswords.sendgridApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* AWS SES Configuration */}
            {formData.provider === 'ses' && (
              <div className="border-t border-gray-200 pt-4 dark:border-gray-700">
                <h4 className="text-sm font-medium text-gray-900 mb-3 dark:text-gray-100">AWS SES Configuration</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                      Region
                    </label>
                    <ThemedSelect
                      value={formData.sesRegion}
                      onChange={(e) => setFormData({ ...formData, sesRegion: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700"
                    >
                      <option value="us-east-1">US East (N. Virginia)</option>
                      <option value="us-west-2">US West (Oregon)</option>
                      <option value="eu-west-1">EU (Ireland)</option>
                      <option value="eu-central-1">EU (Frankfurt)</option>
                      <option value="ap-southeast-1">Asia Pacific (Singapore)</option>
                    </ThemedSelect>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                      Access Key ID
                    </label>
                    <input
                      type="text"
                      value={formData.sesAccessKeyId}
                      onChange={(e) => setFormData({ ...formData, sesAccessKeyId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700"
                      placeholder="AKIAXXXXXXXX"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                      Secret Access Key
                    </label>
                    <div className="relative">
                      <input
                        type={showPasswords.sesSecretAccessKey ? 'text' : 'password'}
                        value={formData.sesSecretAccessKey}
                        onChange={(e) => setFormData({ ...formData, sesSecretAccessKey: e.target.value })}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700"
                        placeholder={editingConfig ? '••••••••' : 'secret key'}
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility('sesSecretAccessKey')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-500"
                      >
                        {showPasswords.sesSecretAccessKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Mailgun Configuration */}
            {formData.provider === 'mailgun' && (
              <div className="border-t border-gray-200 pt-4 dark:border-gray-700">
                <h4 className="text-sm font-medium text-gray-900 mb-3 dark:text-gray-100">Mailgun Configuration</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                      Domain
                    </label>
                    <input
                      type="text"
                      value={formData.mailgunDomain}
                      onChange={(e) => setFormData({ ...formData, mailgunDomain: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700"
                      placeholder="mg.example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                      API Key
                    </label>
                    <div className="relative">
                      <input
                        type={showPasswords.mailgunApiKey ? 'text' : 'password'}
                        value={formData.mailgunApiKey}
                        onChange={(e) => setFormData({ ...formData, mailgunApiKey: e.target.value })}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700"
                        placeholder="key-xxxxxxxx"
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility('mailgunApiKey')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-500"
                      >
                        {showPasswords.mailgunApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Zeptomail Configuration */}
            {formData.provider === 'zeptomail' && (
              <div className="border-t border-gray-200 pt-4 dark:border-gray-700">
                <h4 className="text-sm font-medium text-gray-900 mb-3 dark:text-gray-100">Zeptomail Configuration</h4>
                <p className="text-sm text-gray-500 mb-3 dark:text-gray-400">
                  Get your Send Mail Token from the{' '}
                  <a
                    href="https://mail.zoho.com/portal/portlet/zetadashboard"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:underline inline-flex items-center gap-1"
                  >
                    Zeptomail Dashboard
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </p>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                    Send Mail Token
                  </label>
                  <div className="relative">
                    <input
                      type={showPasswords.zeptomailApiKey ? 'text' : 'password'}
                      value={formData.zeptomailApiKey}
                      onChange={(e) => setFormData({ ...formData, zeptomailApiKey: e.target.value })}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700"
                      placeholder="Zoho-enczapikey xxxxxxxx"
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('zeptomailApiKey')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-500"
                    >
                      {showPasswords.zeptomailApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Microsoft Graph Configuration */}
            {formData.provider === 'microsoft_graph' && (
              <div className="border-t border-gray-200 pt-4 dark:border-gray-700">
                <h4 className="text-sm font-medium text-gray-900 mb-3 dark:text-gray-100">Microsoft Graph Configuration</h4>

                {/* Azure AD Setup Instructions */}
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg dark:bg-blue-900/20 dark:border-blue-800">
                  <div className="flex items-start gap-3">
                    <HelpCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-blue-900 dark:text-blue-200 mb-2">Azure AD App Registration Required</p>
                      <p className="text-blue-800 dark:text-blue-300 mb-3">
                        To use Microsoft Graph for email, you need to register an application in Azure AD.
                      </p>
                      <details className="group">
                        <summary className="cursor-pointer text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-100 font-medium flex items-center gap-1">
                          View setup instructions
                          <ChevronDown className="h-4 w-4 group-open:rotate-180 transition-transform" />
                        </summary>
                        <div className="mt-3 space-y-3 text-blue-800">
                          <div>
                            <p className="font-medium">Step 1: Register an App in Azure Portal</p>
                            <ol className="list-decimal ml-4 mt-1 space-y-1">
                              <li>Go to <a href="https://portal.azure.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">Azure Portal <ExternalLink className="h-3 w-3" /></a></li>
                              <li>Navigate to Azure Active Directory &rarr; App registrations</li>
                              <li>Click "New registration"</li>
                              <li>Enter a name (e.g., "CentraOnePlus Email Integration")</li>
                              <li>Select "Accounts in this organizational directory only"</li>
                              <li>Click "Register"</li>
                            </ol>
                          </div>
                          <div>
                            <p className="font-medium">Step 2: Get Application IDs</p>
                            <ol className="list-decimal ml-4 mt-1 space-y-1">
                              <li>Copy the "Application (client) ID" &rarr; paste as <strong>Client ID</strong> below</li>
                              <li>Copy the "Directory (tenant) ID" &rarr; paste as <strong>Tenant ID</strong> below</li>
                            </ol>
                          </div>
                          <div>
                            <p className="font-medium">Step 3: Create Client Secret</p>
                            <ol className="list-decimal ml-4 mt-1 space-y-1">
                              <li>Go to "Certificates & secrets"</li>
                              <li>Click "New client secret"</li>
                              <li>Add a description and select expiry (24 months recommended)</li>
                              <li>Copy the secret <strong>Value</strong> immediately (shown only once)</li>
                              <li>Paste as <strong>Client Secret</strong> below</li>
                            </ol>
                          </div>
                          <div>
                            <p className="font-medium">Step 4: Configure API Permissions</p>
                            <ol className="list-decimal ml-4 mt-1 space-y-1">
                              <li>Go to "API permissions"</li>
                              <li>Click "Add a permission" &rarr; "Microsoft Graph"</li>
                              <li>Select "Application permissions"</li>
                              <li>Add: <code className="bg-blue-100 px-1 rounded">Mail.Read</code>, <code className="bg-blue-100 px-1 rounded">Mail.Send</code>, <code className="bg-blue-100 px-1 rounded">User.Read.All</code></li>
                              <li>Click "Grant admin consent for [your org]"</li>
                            </ol>
                          </div>
                          <div className="pt-2 border-t border-blue-200">
                            <p className="text-xs text-blue-700">
                              <strong>Note:</strong> The email address above must be a valid mailbox in your Microsoft 365 tenant.
                              Admin consent is required for application permissions.
                            </p>
                          </div>
                        </div>
                      </details>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                      Tenant ID
                    </label>
                    <input
                      type="text"
                      value={formData.graphTenantId}
                      onChange={(e) => setFormData({ ...formData, graphTenantId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700"
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                      Client ID
                    </label>
                    <input
                      type="text"
                      value={formData.graphClientId}
                      onChange={(e) => setFormData({ ...formData, graphClientId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700"
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                      Client Secret
                    </label>
                    <div className="relative">
                      <input
                        type={showPasswords.graphClientSecret ? 'text' : 'password'}
                        value={formData.graphClientSecret}
                        onChange={(e) => setFormData({ ...formData, graphClientSecret: e.target.value })}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700"
                        placeholder={editingConfig?.providerConfig?.clientSecret ? '••••••••••••••••' : 'Enter client secret'}
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility('graphClientSecret')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-500"
                      >
                        {showPasswords.graphClientSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {editingConfig?.providerConfig?.clientSecret && !formData.graphClientSecret && (
                      <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">Secret is saved. Leave blank to keep current value.</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Email Signature */}
            <div className="border-t border-gray-200 pt-4 dark:border-gray-700">
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                Email Signature
              </label>
              <textarea
                value={formData.signature}
                onChange={(e) => setFormData({ ...formData, signature: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700"
                rows={3}
                placeholder="Best regards,&#10;Support Team"
              />
            </div>

            {/* Default Setting */}
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
              <input
                type="checkbox"
                checked={formData.isDefault}
                onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-700"
              />
              Set as default email account
            </label>

            {/* Actions */}
            <div className="flex items-center gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? (
                  <>
                    <Spinner size="sm" className="mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    {editingConfig ? 'Update' : 'Create'}
                  </>
                )}
              </Button>
              <Button variant="outline" onClick={handleCancel}>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              {editingConfig && (
                <Button
                  variant="outline"
                  onClick={() => handleTest(editingConfig.id)}
                  disabled={testingConfig === editingConfig.id || isFormDirty || isInitializing}
                  title={isFormDirty ? 'Save changes before testing connection' : undefined}
                >
                  {testingConfig === editingConfig.id ? (
                    <>
                      <Spinner size="sm" className="mr-2" />
                      Testing...
                    </>
                  ) : isInitializing ? (
                    <>
                      <Spinner size="sm" className="mr-2" />
                      Initializing...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Test Connection
                    </>
                  )}
                </Button>
              )}
              {isFormDirty && editingConfig && formData.provider === 'microsoft_graph' && (
                <span className="text-xs text-amber-600 ml-2">
                  Save changes first to test connection
                </span>
              )}
            </div>
            {/* Initialization Result */}
            {initResult && formData.provider === 'microsoft_graph' && (
              <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
                initResult.success
                  ? 'bg-green-50 text-green-700 border border-green-200'
                  : 'bg-red-50 text-red-700 border border-red-200'
              }`}>
                {initResult.success ? (
                  <CheckCircle className="h-4 w-4 flex-shrink-0" />
                ) : (
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                )}
                <span>{initResult.message}</span>
              </div>
            )}
            {/* Test Result */}
            {testResult && editingConfig && testResult.configId === editingConfig.id && (
              <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
                testResult.success
                  ? 'bg-green-50 text-green-700 border border-green-200'
                  : 'bg-red-50 text-red-700 border border-red-200'
              }`}>
                {testResult.success ? (
                  <CheckCircle className="h-4 w-4 flex-shrink-0" />
                ) : (
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                )}
                <span>{testResult.message}</span>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Email Configurations List */}
      {configs.length === 0 && !showForm ? (
        <Card className="p-8 text-center">
          <Mail className="h-12 w-12 text-gray-400 mx-auto mb-4 dark:text-gray-500" />
          <h3 className="text-sm font-medium text-gray-900 mb-1 dark:text-gray-100">No email accounts configured</h3>
          <p className="text-sm text-gray-500 mb-4 dark:text-gray-400">Add an email account to start receiving and sending tickets via email</p>
          <Button onClick={handleCreateNew}>
            <Plus className="h-4 w-4 mr-2" />
            Add Email Account
          </Button>
        </Card>
      ) : (
        <div className="space-y-3">
          {configs.map((config) => (
            <Card key={config.id} className="overflow-hidden">
              <div
                className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50"
                onClick={() => setExpandedConfig(expandedConfig === config.id ? null : config.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {expandedConfig === config.id ? (
                      <ChevronDown className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                    )}
                    <div className="h-10 w-10 rounded-lg bg-primary-100 flex items-center justify-center">
                      <Mail className="h-5 w-5 text-primary-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">{config.name}</h4>
                        {config.isDefault && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-100 text-primary-700 text-xs font-medium rounded">
                            <Star className="h-3 w-3" />
                            Default
                          </span>
                        )}
                        {!config.isActive && (
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded dark:bg-gray-800 dark:text-gray-400">
                            Inactive
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{config.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    {testResult?.configId === config.id && (
                      <span className={`text-xs px-2 py-1 rounded ${testResult.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {testResult.success ? 'Test passed' : 'Test failed'}
                      </span>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleTest(config.id)}
                      disabled={testingConfig === config.id}
                    >
                      {testingConfig === config.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Plug className="h-4 w-4" />
                      )}
                    </Button>
                    {!config.isDefault && (
                      <Button variant="ghost" size="sm" onClick={() => handleSetDefault(config.id)}>
                        <Star className="h-4 w-4" />
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(config)}>
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(config.id)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {expandedConfig === config.id && (
                <div className="px-4 pb-4 pt-0 border-t border-gray-100 dark:border-gray-800">
                  <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Provider:</span>{' '}
                      <span className="text-gray-900 capitalize dark:text-gray-100">{config.provider || 'SMTP'}</span>
                    </div>
                    {config.smtpHost && (
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">SMTP:</span>{' '}
                        <span className="text-gray-900 dark:text-gray-100">{config.smtpHost}:{config.smtpPort}</span>
                      </div>
                    )}
                    {config.imapHost && (
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">IMAP:</span>{' '}
                        <span className="text-gray-900 dark:text-gray-100">{config.imapHost}:{config.imapPort}</span>
                      </div>
                    )}
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Created:</span>{' '}
                      <span className="text-gray-900 dark:text-gray-100">{new Date(config.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                  {config.signature && (
                    <div className="mt-4">
                      <span className="text-gray-500 text-sm dark:text-gray-400">Signature:</span>
                      <pre className="mt-1 text-sm text-gray-900 bg-gray-50 p-2 rounded whitespace-pre-wrap dark:bg-gray-900 dark:text-gray-100">
                        {config.signature}
                      </pre>
                    </div>
                  )}
                  <UndoSendWindowSetting configId={config.id} />
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
